import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { monitorService } from 'services/monitor'
import { RequestState } from 'shared/enums'
import type { MonitorRun, ReadMonitorsResponse } from 'shared/types'
import type { PropertyStoreMeta } from 'shared/types'
import type { Monitor } from 'shared/types'
import { DateTime } from 'luxon'
import { Context } from 'shared/types/Context'

interface MainBarSettingsModel {
    title?: string
    back?: {
        url?: string
        toParent?: boolean
    },
    sections?: {
        center?: {
            content: JSX.Element
        }
    }
    showProfileButton?: boolean
    showHamburgerButton? : boolean
}

interface SetPageStateParams{
    name: string
    state: any
}

interface NotificationsSettingsModel {
    notificationsStatus?: boolean
    dialogOpen: boolean
    checkingStatusNotifications: {
        lastApplicationVersionForChecking?: string
        lastDateForChecking?: DateTime
        showReminder?: boolean
    }
}

interface ApplicationStateData {
    drawerOpen: boolean
    applicationReady: boolean
    user?: object
    monitors?: Monitor[]
    monitorsCount?: number
    monitorRunForCompetitors?: MonitorRun
    hostServiceForAvailability?: any
    needShowInstallationInstruction: boolean
    title: string
    mainBarRef: any
    mainBarSettings?: MainBarSettingsModel | undefined
    parentUrl?: string
    notificationsSettings: NotificationsSettingsModel 
    applicationBottomNavigationRef: any
    showMainBackDrop: boolean,
    zIndex: number,
    mode: 'property' | 'market',
    pagesState: any
    context: Context | undefined
}

interface ApplicationStateMeta {
    monitors: PropertyStoreMeta,
    monitorsCount: PropertyStoreMeta,
}

interface ApplicationState {
    data: ApplicationStateData
    meta: ApplicationStateMeta
}

const initialState: ApplicationState = {
    data: {
        drawerOpen: false,
        applicationReady: false,
        monitors: [],
        monitorsCount: 0,
        monitorRunForCompetitors: undefined,
        hostServiceForAvailability: undefined,
        needShowInstallationInstruction: true,
        title: '',
        mainBarSettings: undefined,
        mainBarRef: undefined,
        parentUrl: undefined,
        notificationsSettings: {
            dialogOpen: false,
            notificationsStatus: undefined,
            checkingStatusNotifications: {
                lastApplicationVersionForChecking: undefined,
                lastDateForChecking: undefined,
                showReminder: true
            }
        },
        applicationBottomNavigationRef: undefined,
        mode: 'property',
        pagesState: {},
        showMainBackDrop: false,
        zIndex: 500,
        context: {
            filters:{
                bedrooms: ['1+'],
                beds: ['1+'],
                pets: ['1+']
            },
            spyLaunchSettings:{
                   params: {
                    daysAhead: 3
                    },
                    mode: 'sliding'
            }                    
        }
    },
    meta: {
        monitors: {
            requestId: undefined,
            status: RequestState.Idle,
        },
        monitorsCount: {
            requestId: undefined,
            status: RequestState.Idle,
        }
    }
}

export interface ReadMonitorsThunkParams {
    pagination: {
        page: number,
        pageSize: number
    }
}

export const readMonitors = createAsyncThunk(
    'application/readMonitors',
    async function (params: ReadMonitorsThunkParams, { rejectWithValue }) {
        try {
            const response: ReadMonitorsResponse = await monitorService.readMonitors(params);

            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const applicationSlice = createSlice({
    name: 'Application',
    initialState,
    reducers: {
        setApplicationReady(state, action: PayloadAction<boolean>) {
            state.data.applicationReady = action.payload;
        },
        setDrawerOpen(state, action: PayloadAction<boolean>) {
            state.data.drawerOpen = action.payload;
        },
        setUser: (state, action: PayloadAction<object>) => {
            state.data.user = action.payload;
        },
        setTitle: (state, action: PayloadAction<string>) => {
            state.data.title = action.payload;
        },
        setParentUrl: (state, action: PayloadAction<string | undefined>) => {
            state.data.parentUrl = action.payload;
        },
        setMainBarSettings: (state, action: PayloadAction<MainBarSettingsModel | undefined>) => {
            state.data.mainBarSettings = action.payload;
        },
        setMonitorRunForCompetitors: (state, action: PayloadAction<MonitorRun | undefined>) => {
            state.data.monitorRunForCompetitors = action.payload;
        },
        setHostServiceForAvailability: (state, action: PayloadAction<MonitorRun | undefined>) => {
            state.data.hostServiceForAvailability = action.payload;
        },
        setNeedShowInstallationInstruction: (state, action: PayloadAction<boolean>) => {
            state.data.needShowInstallationInstruction = action.payload;
        },
        setNotificationStatus: (state, action: PayloadAction<boolean>) => {
            state.data.notificationsSettings.notificationsStatus = action.payload;
        },
        setShowNotificationReminder: (state, action: PayloadAction<boolean>) => {
            state.data.notificationsSettings.checkingStatusNotifications.showReminder = action.payload;
        },
        setApplicationBottomNavigationRef: (state, action: PayloadAction<any>) => {
            state.data.applicationBottomNavigationRef = action.payload;
        },
        setMode: (state, action: PayloadAction<any>) => {
            state.data.mode = action.payload;
        },
        setMainBarRef: (state, action: PayloadAction<HTMLElement>) => {
            state.data.mainBarRef = action.payload;
        },
        setNotificationsSettingsDialogOpen: (state, action: PayloadAction<boolean>) => {
            state.data.notificationsSettings.dialogOpen = action.payload
        },
        setPageState: (state, action: PayloadAction<SetPageStateParams>) => {           
            state.data.pagesState = { ...state.data.pagesState, [action.payload.name]:{...action.payload.state}}
        },
        setShowMainBackDrop: (state, action: PayloadAction<boolean>) => {           
            state.data.showMainBackDrop = action.payload;
        },
        incrementZindex: (state, action: PayloadAction<void>) => {           
            state.data.zIndex = state.data.zIndex++;
        },
        decrementZindex: (state, action: PayloadAction<void>) => {           
            state.data.zIndex = state.data.zIndex--;
        },
        setContext: (state, action: PayloadAction<Context>) => {
            state.data.context = action.payload
        },
        updateContext: (state, action: PayloadAction<Context>) => {
            state.data.context = {
                ...state.data.context,
                ...action.payload
            }
        },
        setContextLocation: (state, action: PayloadAction<Location>) => {
             if (action.payload === undefined){
                    state.data.context = {
                    ...state.data.context,
                    ['location']: undefined            
                }
                
                return 
             }

            state.data.context = {
                ...state.data.context,
                ['location']: {
                    ...action.payload
                }            
            }
        },
        setContextSpyLaunchSettings: (state, action: PayloadAction<Location>) => {
            state.data.context = {
                ...state.data.context,
                ['spyLaunchSettings']: {
                    ...action.payload
                }            
            }
        },
        setContextFilters: (state, action: PayloadAction<any>) => {
            if (action.payload === undefined){
                    state.data.context = {
                    ...state.data.context,
                    ['filters']: undefined            
                }

                return
            }

            state.data.context = {
                ...state.data.context,
                ['filters']: {
                    ...action.payload
                }            
            }
        },
        setContextSpyId: (state, action: PayloadAction<number | undefined>) => {
            state.data.context = {
                ...state.data.context,
                ['spyId']: action.payload            
            }
        },
        setContextLocationSearchString: (state, action: PayloadAction<string | undefined>) => {
            state.data.context = {
                ...state.data.context,
                ['locationSearchString']: action.payload            
            }
        }

    },
    extraReducers: (builder) => {
        builder
            .addCase(readMonitors.pending, (state, action) => {
                state.meta.monitors.status = RequestState.Pending;
                state.meta.monitorsCount.status = RequestState.Pending;
            })
            .addCase(readMonitors.fulfilled, (state, action) => {
                state.meta.monitors.status = RequestState.Success;
                state.meta.monitorsCount.status = RequestState.Success;
                state.data.monitors = action.payload.list;
                state.data.monitorsCount = action.payload.count;
            })
    }
})

export const {
    setTitle,
    setApplicationReady,
    setDrawerOpen,
    setUser,
    setMonitorRunForCompetitors,
    setHostServiceForAvailability,
    setNeedShowInstallationInstruction,
    setMainBarSettings,
    setParentUrl,
    setApplicationBottomNavigationRef,
    setMode,
    setNotificationsSettingsDialogOpen,
    setPageState,
    setShowMainBackDrop,
    incrementZindex,
    decrementZindex,
    setContext,
    updateContext,
    setContextLocation,
    setContextSpyLaunchSettings,
    setContextFilters,
    setContextSpyId,
    setContextLocationSearchString
} = applicationSlice.actions
export const selectDrawerOpen = (state: RootState) => state.application.data.drawerOpen;
export const selectApplicationReady = (state: RootState) => state.application.data.applicationReady;
export const selectUser = (state: RootState) => state.application.data.user;
export const selectMonitorRunForCompetitors = (state: RootState) => state.application.data.monitorRunForCompetitors;
export const selectHostServiceForAvailability = (state: RootState) => state.application.data.hostServiceForAvailability;
export const selectMonitorsCount = (state: RootState) => state.application.data.monitorsCount;
export const selectTitle = (state: RootState) => state.application.data.title;
export const selectNeedShowInstallationInstruction = (state: RootState) => state.application.data.needShowInstallationInstruction;
export const selectMainBarSettings = (state: RootState) => state.application.data.mainBarSettings
export const selectParentUrl = (state: RootState) => state.application.data.parentUrl
export const selectShowNotificationReminder = (state: RootState) => state.application.data.notificationsSettings.checkingStatusNotifications.showReminder
export const selectApplicationBottomNavigationRef = (state: RootState) => state.application.data.applicationBottomNavigationRef
export const selectMode = (state: RootState) => state.application.data.mode
export const selectNotificationsSettingsDialogOpen = (state: RootState) => state.application.data.notificationsSettings.dialogOpen
export const selectPageState = (state: RootState, pageName: string) => state.application.data.pagesState[pageName]
export const selectShowMainBackDrop = (state: RootState) => state.application.data.showMainBackDrop
export const selectZIndex = (state: RootState) => state.application.data.zIndex
export const selectCurrentSpyId = (state: RootState) => {
    const pagesState: Object = state.application.data.pagesState
    let currentSpyIdState: any = undefined

    for (const [pageName, pageState] of Object.entries(pagesState)) {
        if (pageState['spyId'] === undefined){
            continue
        }

        if (currentSpyIdState === undefined){
            currentSpyIdState = pageState['spyId']
            continue
        }

        if (currentSpyIdState.timeStamp < pageState['spyId'].timeStamp){
            currentSpyIdState = pageState['spyId']
        }
    }

    return currentSpyIdState
}

export const selectCurrentLocation = (state: RootState) => {
    const pagesState: Object = state.application.data.pagesState
    let currentLocationState: any = undefined

    for (const [pageName, pageState] of Object.entries(pagesState)) {
        if (pageState['location'] === undefined){
            continue
        }

        if (currentLocationState === undefined){
            currentLocationState = pageState['location']
            continue
        }

        if (currentLocationState.timeStamp < pageState['location'].timeStamp){
            currentLocationState = pageState['location']
        }
    }

    return currentLocationState
}

export const selectCurrentFilter = (state: RootState) => {
    const pagesState: Object = state.application.data.pagesState
    let currentFilterState: any = undefined

    for (const [pageName, pageState] of Object.entries(pagesState)) {
        if (pageState['filter'] === undefined){
            continue
        }

        if (currentFilterState === undefined){
            currentFilterState = pageState['filter']
            continue
        }

        if (currentFilterState.timeStamp < pageState['filter'].timeStamp){
            currentFilterState = pageState['filter']
        }
    }

    return currentFilterState
}

export const selectCurrentSpylaunchSettings = (state: RootState) => {
    const pagesState: Object = state.application.data.pagesState
    let currentFilterState: any = undefined

    for (const [pageName, pageState] of Object.entries(pagesState)) {
        if (pageState['spyLaunchSettings'] === undefined){
            continue
        }

        if (currentFilterState === undefined){
            currentFilterState = pageState['spyLaunchSettings']
            continue
        }

        if (currentFilterState.timeStamp < pageState['spyLaunchSettings'].timeStamp){
            currentFilterState = pageState['spyLaunchSettings']
        }
    }

    return currentFilterState
}

export const selectContext = (state: RootState) => state.application.data.context
export const selectContextLocation = (state: RootState) => state.application.data.context?.location
export const selectContextLocationSearchString = (state: RootState) => state.application.data.context?.locationSearchString
export const selectContextSpyLaunchSettings = (state: RootState) => state.application.data.context?.spyLaunchSettings
export const selectContextFilters = (state: RootState) => state.application.data.context?.filters
export const selectContextSpyId = (state: RootState) => state.application.data.context?.spyId

export default applicationSlice.reducer